<template>
  <div class="addenda-fields-form">
    <label>¿Qué campos cambia esta adenda?
      <small v-if="optional" class="text-muted">(opcional)</small>
    </label>
    <div class="flex flex-wrap mt-2" style="gap: 6px 16px;">
      <wz-checkbox v-for="opt in fieldOptions" :key="opt.key" v-model="sel[opt.key]">
        {{ opt.label }}
      </wz-checkbox>
    </div>

    <div v-if="sel.BASE_SALARY" class="mt-3">
      <label>Nuevo sueldo (S/) *</label>
      <input v-model.number="newBaseSalary" type="number" min="0" step="0.01"
        class="w-full mt-2 addenda-input" placeholder="Ej.: 4000.00" />
    </div>

    <div v-if="sel.POSITION" class="mt-3">
      <label>Nuevo cargo *</label>
      <wz-select class="mt-2" :items="positionOptions" v-model="newPositionId" defaultOption="Elegir cargo" />
    </div>

    <div v-if="sel.AREA" class="mt-3">
      <label>Nueva área *</label>
      <wz-select class="mt-2" :items="areaOptions" v-model="newAreaId" defaultOption="Elegir área" />
    </div>

    <div v-if="sel.CHANNEL" class="mt-3">
      <label>Nuevo canal *</label>
      <wz-select class="mt-2" :items="channelOptions" v-model="newChannelId" defaultOption="Elegir canal" />
    </div>

    <div v-if="sel.EXPENSE_LINE" class="mt-3">
      <label>Nueva línea de gasto *</label>
      <wz-select class="mt-2" :items="expenseLineOptions" v-model="newExpenseLineId" defaultOption="Elegir línea de gasto" />
    </div>

    <div v-if="sel.WORK_SITE" class="mt-3">
      <label>Nueva sede *</label>
      <wz-select class="mt-2" :items="workSiteOptions" v-model="newWorkSiteId" defaultOption="Elegir sede" />
      <small v-if="!companyId" class="text-muted d-block mt-1">Seleccione una empresa para ver las sedes.</small>
    </div>

    <div v-if="sel.DIRECT_MANAGER" class="mt-3">
      <label>Nuevo jefe directo *</label>
      <wz-select class="mt-2" :items="managerOptions" v-model="newDirectManagerId" defaultOption="Elegir jefe directo" />
    </div>

    <div v-if="sel.HR_RESPONSIBLE_EMAIL" class="mt-3">
      <label>Nuevo responsable (email) *</label>
      <input v-model="newHrResponsibleEmail" type="email"
        class="w-full mt-2 addenda-input" placeholder="responsable@empresa.com" />
    </div>
  </div>
</template>

<script>
// DEF-004: formulario reutilizable de "qué campos cambia la adenda + valores nuevos". Antes estaba
// duplicado en ContractAddendaSection (detalle), ContractEditor (renovación) y faltaba en el masivo.
// v-model: objeto { changedFields:[], newBaseSalary, newPositionId, newAreaId, newChannelId,
// newExpenseLineId, newWorkSiteId, newDirectManagerId, newHrResponsibleEmail }.
// Resetear desde el host cambiando la prop :key (re-monta e inicializa desde `value`).
import PositionService from '@/core/services/PositionService'
import AreaService from '@/core/services/AreaService'
import ChannelService from '@/core/services/ChannelService'
import ExpenseLineService from '@/core/services/ExpenseLineService'
import WorkSiteService from '@/core/services/WorkSiteService'
import EmployeeService from '@/core/services/EmployeeService'

export const NON_DATE_AWARE_FIELDS = ['AREA', 'CHANNEL', 'EXPENSE_LINE', 'WORK_SITE', 'DIRECT_MANAGER', 'HR_RESPONSIBLE_EMAIL']

export default {
  name: 'AddendaFieldsForm',
  props: {
    value: { type: Object, default: () => ({}) },
    companyId: { type: [Number, String], default: null },
    optional: { type: Boolean, default: false },
  },
  data() {
    const v = this.value || {}
    const cf = Array.isArray(v.changedFields) ? v.changedFields : []
    return {
      positions: [], areas: [], channels: [], expenseLines: [], workSites: [], managers: [],
      sel: {
        BASE_SALARY: cf.includes('BASE_SALARY'),
        POSITION: cf.includes('POSITION'),
        AREA: cf.includes('AREA'),
        CHANNEL: cf.includes('CHANNEL'),
        EXPENSE_LINE: cf.includes('EXPENSE_LINE'),
        WORK_SITE: cf.includes('WORK_SITE'),
        DIRECT_MANAGER: cf.includes('DIRECT_MANAGER'),
        HR_RESPONSIBLE_EMAIL: cf.includes('HR_RESPONSIBLE_EMAIL'),
      },
      newBaseSalary: v.newBaseSalary ?? null,
      newPositionId: v.newPositionId ?? null,
      newAreaId: v.newAreaId ?? null,
      newChannelId: v.newChannelId ?? null,
      newExpenseLineId: v.newExpenseLineId ?? null,
      newWorkSiteId: v.newWorkSiteId ?? null,
      newDirectManagerId: v.newDirectManagerId ?? null,
      newHrResponsibleEmail: v.newHrResponsibleEmail ?? null,
    }
  },
  computed: {
    fieldOptions() {
      return [
        { key: 'BASE_SALARY', label: 'Sueldo' },
        { key: 'POSITION', label: 'Cargo' },
        { key: 'AREA', label: 'Área' },
        { key: 'CHANNEL', label: 'Canal' },
        { key: 'EXPENSE_LINE', label: 'Línea de gasto' },
        { key: 'WORK_SITE', label: 'Sede' },
        { key: 'DIRECT_MANAGER', label: 'Jefe directo' },
        { key: 'HR_RESPONSIBLE_EMAIL', label: 'Responsable' },
      ]
    },
    positionOptions()    { return this.positions.map(p => ({ key: p.id, label: p.description })) },
    areaOptions()        { return this.areas.map(a => ({ key: a.id, label: a.description })) },
    channelOptions()     { return this.channels.map(c => ({ key: c.id, label: c.name })) },
    expenseLineOptions() { return this.expenseLines.map(e => ({ key: e.id, label: e.name })) },
    workSiteOptions()    { return this.workSites.map(w => ({ key: w.id, label: w.name })) },
    managerOptions()     { return this.managers.map(m => ({ key: m.id, label: m.description || m.label })) },
    changedFields() {
      return Object.keys(this.sel).filter(k => this.sel[k])
    },
    composed() {
      const s = this.sel
      return {
        changedFields: this.changedFields,
        newBaseSalary: s.BASE_SALARY ? this.newBaseSalary : null,
        newPositionId: s.POSITION ? this.newPositionId : null,
        newAreaId: s.AREA ? this.newAreaId : null,
        newChannelId: s.CHANNEL ? this.newChannelId : null,
        newExpenseLineId: s.EXPENSE_LINE ? this.newExpenseLineId : null,
        newWorkSiteId: s.WORK_SITE ? this.newWorkSiteId : null,
        newDirectManagerId: s.DIRECT_MANAGER ? this.newDirectManagerId : null,
        newHrResponsibleEmail: s.HR_RESPONSIBLE_EMAIL ? ((this.newHrResponsibleEmail || '').trim() || null) : null,
      }
    },
  },
  watch: {
    composed: {
      deep: true,
      handler(v) { this.$emit('input', v) },
    },
    companyId() {
      this.loadWorkSites()
      // La sede depende de la empresa: al cambiarla, limpiar la selección previa.
      this.newWorkSiteId = null
    },
  },
  methods: {
    // Devuelve un mensaje de error si algún campo seleccionado no tiene valor válido; null si todo OK.
    validate() {
      const s = this.sel
      if (s.BASE_SALARY && !(this.newBaseSalary > 0)) return 'El nuevo sueldo debe ser mayor a 0'
      if (s.POSITION && !this.newPositionId) return 'Selecciona el nuevo cargo'
      if (s.AREA && !this.newAreaId) return 'Selecciona la nueva área'
      if (s.CHANNEL && !this.newChannelId) return 'Selecciona el nuevo canal'
      if (s.EXPENSE_LINE && !this.newExpenseLineId) return 'Selecciona la nueva línea de gasto'
      if (s.WORK_SITE && !this.newWorkSiteId) return 'Selecciona la nueva sede'
      if (s.DIRECT_MANAGER && !this.newDirectManagerId) return 'Selecciona el nuevo jefe directo'
      if (s.HR_RESPONSIBLE_EMAIL && !(this.newHrResponsibleEmail || '').trim()) return 'Ingresa el responsable (email)'
      return null
    },
    async loadCatalogs() {
      const safe = async (fn) => { try { const r = await fn(); return r.payload || [] } catch (e) { return [] } }
      const [pos, are, cha, exp, mgr] = await Promise.all([
        safe(() => PositionService.listAll()),
        safe(() => AreaService.listActive()),
        safe(() => ChannelService.listActive()),
        safe(() => ExpenseLineService.listActive()),
        safe(() => EmployeeService.listActiveManagers(null)),
      ])
      this.positions = pos
      this.areas = are
      this.channels = cha
      this.expenseLines = exp
      this.managers = mgr
      await this.loadWorkSites()
    },
    async loadWorkSites() {
      if (!this.companyId) { this.workSites = []; return }
      try {
        const r = await WorkSiteService.listByCompanyAndStatus(this.companyId, 1)
        this.workSites = r.payload || []
      } catch (e) {
        this.workSites = []
      }
    },
  },
  created() {
    this.loadCatalogs()
  },
}
</script>

<style lang="stylus" scoped>
.addenda-input
  box-sizing border-box
  padding 8px
  border 1px solid var(--admin-border-color)
  border-radius 6px
  font-family inherit
  font-size 14px
</style>
